"use client";

import { useRef } from "react";

interface PttButtonProps {
  disabled: boolean;
  mode: "idle" | "active";
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
}

// Hexagon clip-path, point-up.
const HEX = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

export function PttButton({
  disabled,
  mode,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
  onPointerLeave,
}: PttButtonProps) {
  // Track if we're currently being pressed. On mobile, pointerLeave can fire
  // during touch drag before pointerUp - we ignore leave while pressed.
  const pressedRef = useRef(false);

  const handlePointerDown = (e: React.PointerEvent) => {
    pressedRef.current = true;
    onPointerDown(e);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    pressedRef.current = false;
    onPointerUp(e);
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    pressedRef.current = false;
    onPointerCancel(e);
  };

  // Only fire onPointerLeave if we're NOT pressed. On mobile, a touch drag
  // outside the button triggers pointerLeave but NOT pointerUp - we must
  // NOT end the session in that case. Only pointerUp/pointerCancel should end.
  const handlePointerLeave = (e: React.PointerEvent) => {
    if (!pressedRef.current) {
      onPointerLeave(e);
    }
  };

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerLeave}
      disabled={disabled}
      aria-label="Mantén pulsado para grabar y suelta para enviar"
      style={{ clipPath: HEX, touchAction: "none" }}
      className={`group relative grid h-20 w-20 cursor-pointer touch-none select-none place-items-center border transition-all duration-200 ease-out sm:h-24 sm:w-24 ${
        mode === "active"
          ? "scale-95 border-cyan-bright bg-cyan/15 shadow-[0_0_40px_rgba(0,229,255,0.45)]"
          : disabled
            ? "cursor-not-allowed border-line bg-bg/40 opacity-50"
            : "border-cyan/40 bg-bg/40 hover:border-cyan-bright hover:bg-cyan/[0.06] hover:shadow-[0_0_24px_rgba(0,229,255,0.25)]"
      }`}
    >
      <MicIcon active={mode === "active"} />
    </button>
  );
}

function MicIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`relative z-10 transition-colors duration-200 ${
        active ? "text-cyan-bright" : "text-cyan/70 group-hover:text-cyan-bright"
      }`}
    >
      <rect
        x="9"
        y="3"
        width="6"
        height="12"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
